import React, { useEffect, useRef, useState } from "react";
import { Play, Pause } from "lucide-react";
import { api } from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Hidden until an admin uploads or generates the clip at /admin/site.
export default function FounderVoiceNote() {
  const [voice, setVoice] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [time, setTime] = useState(0);
  const ref = useRef(null);

  useEffect(() => {
    api.get("/site/founder-voice").then((r) => setVoice(r.data)).catch(() => {});
  }, []);

  if (!voice?.available) return null;
  const captions = voice.captions || [];
  const line = captions.find((c) => time >= c.start && time < c.end + 0.25);

  const toggle = () => {
    const a = ref.current;
    if (a.paused) a.play(); else a.pause();
  };

  return (
    <div className="mt-8 border border-obsidian-600 bg-obsidian-800/60 p-5 max-w-lg" data-testid="founder-voice-note">
      <div className="flex items-center gap-4">
        <button
          onClick={toggle}
          aria-label={playing ? "Pause founder voice note" : "Play founder voice note"}
          data-testid="founder-voice-play-btn"
          className="h-12 w-12 shrink-0 rounded-full bg-terracotta hover:bg-terracotta-deep text-obsidian-900 flex items-center justify-center transition-colors"
        >
          {playing ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5 ml-0.5" />}
        </button>
        <div className="flex-1">
          <p className="font-serif text-lg text-bone-100 leading-tight">A 30-second note from Danny</p>
          <p className="font-sans text-[0.65rem] uppercase tracking-[0.24em] text-bone-500 mt-1">A 30-second note from Uncle Danny</p>
          <div className="mt-3 h-[2px] bg-obsidian-600" aria-hidden="true">
            <div className="h-full bg-terracotta transition-[width] duration-200" style={{ width: `${progress}%` }} />
          </div>
        </div>
      </div>
      {captions.length > 0 && (playing || time > 0) && (
        <p aria-live="polite" className="mt-4 min-h-[3.5rem] font-serif italic text-lg text-bone-100 leading-snug" data-testid="founder-voice-caption">
          {line && <span key={line.start} className="inline-block animate-fade-up">{line.text}</span>}
        </p>
      )}
      <audio
        ref={ref}
        src={`${BACKEND}${voice.audio_url}`}
        preload="none"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => { setPlaying(false); setProgress(100); }}
        onTimeUpdate={(e) => {
          setTime(e.target.currentTime);
          setProgress((e.target.currentTime / (e.target.duration || 1)) * 100);
        }}
        data-testid="founder-voice-audio"
      />
      {voice.transcript && (
        <details className="mt-4">
          <summary className="cursor-pointer font-sans text-xs uppercase tracking-[0.2em] text-bone-400 hover:text-terracotta" data-testid="founder-voice-transcript-toggle">
            Read the transcript
          </summary>
          <p className="mt-3 font-serif italic text-bone-300 leading-relaxed" data-testid="founder-voice-transcript">{voice.transcript}</p>
        </details>
      )}
    </div>
  );
}
